import api from 'api/config';

/**
 * AI Service for context enrichment and chat functionality
 * Provides methods to interact with the AI endpoints
 *
 * Backend endpoints:
 * - POST /ai/enrich - Context enrichment
 * - POST /ai/chat - Chat with optional streaming
 *
 * Supported Chat Contexts:
 * - 'default': General purpose chat
 * - 'contract_builder': Contract creation with SDUI responses
 * - 'support': Customer support assistance
 * - 'project_assistant': Project management help
 * - 'code_review': Code review and analysis
 */
export const aiService = {
  /**
   * Context enrichment - enhances provided context with AI insights
   * @param {string} context - The context to enrich
   * @param {string} query - The specific query about the context
   * @param {string} model - Optional model to use (defaults to server default)
   * @returns {Promise} Response with enriched content
   */
  enrichContext: async (context, query, model = null) => {
    try {
      const payload = {
        context,
        query,
        ...(model && { model }),
      };

      const response = await api.post('/ai/enrich', payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Context enrichment failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to enrich context',
        details: error.response?.data?.details,
      };
    }
  },

  /**
   * Chat with AI - main chat interface with optional context and streaming
   * @param {string} message - The chat message
   * @param {Object} options - Chat options
   * @param {string} options.sessionId - Session ID for context continuity
   * @param {string} options.chatContext - Chat context ('default', 'contract_builder', etc.)
   * @param {Object} options.contextData - Context-specific data
   * @param {string} options.model - Optional model to use
   * @param {boolean} options.stream - Whether to use streaming
   * @param {Object} options.callbacks - Streaming callbacks
   * @returns {Promise} Response with AI chat response
   */
  chat: async (message, options = {}) => {
    const {
      sessionId = `session-${Date.now()}`,
      chatContext = 'default',
      contextData = null,
      model = null,
      stream = false,
      webSearchEnabled = false,
      callbacks = {},
    } = options;
    try {
      const requestData = {
        message,
        session_id: sessionId,
        chat_context: chatContext || 'default',
        stream: !!stream,
        web_search_enabled: webSearchEnabled, // Add web search flag
      };

      // Only add model if it's a valid string
      if (model && typeof model === 'string') {
        requestData.model = model;
      }

      // Only add context_data if it's a valid object
      if (
        contextData &&
        typeof contextData === 'object' &&
        !Array.isArray(contextData)
      ) {
        requestData.context_data = contextData;
      }

      // Add context string for backward compatibility
      requestData.context = chatContext || 'default';

      if (stream) {
        const enhancedCallbacks = aiService.createSSECallbacks(
          callbacks,
          chatContext
        );
        return api.stream('/ai/chat', requestData, enhancedCallbacks);
      } else {
        const response = await api.post('/ai/chat', requestData);
        // Return the response in the expected format for compatibility
        return {
          session_id: response.data.session_id,
          message_id: response.data.message_id,
          response: response.data.response,
          model: response.data.model,
          timestamp: response.data.timestamp,
          is_stream: response.data.is_stream || false,
          is_complete: response.data.is_complete || true,
          sdui: response.data.sdui,
          context_data: response.data.context_data,
        };
      }
    } catch (error) {
      throw new Error(
        error.response?.data?.error ||
          error.message ||
          'Failed to send chat message with context'
      );
    }
  },

  /**
   * Create SSE callbacks for streaming responses
   * Simplified handler that processes SSE events from the backend
   */
  createSSECallbacks(callbacks = {}, context = null) {
    const streamState = {
      currentContent: '',
      currentSessionId: null,
      currentMessageId: null,
      isSDUIContext:
        context === 'contract_builder_sdui' || context === 'contract_builder',
    };

    return {
      onMessage: (chunk) => {
        const lines = chunk.split('\n');
        let currentEvent = null;

        for (const line of lines) {
          if (!line.trim()) continue;

          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            try {
              const data = JSON.parse(line.slice(5).trim());
              this.processSSEEvent(currentEvent, data, streamState, callbacks);
            } catch (e) {
              console.error('[AI Service] SSE parse error:', e);
            }
          }
        }
      },

      onError: (error) => {
        console.error('[AI Service] SSE error:', error);
        callbacks.onError?.(error);
      },

      onComplete: () => {
        // Note: onStreamComplete is called from processSSEEvent when 'complete' event is received
        // Only call onComplete here for any cleanup, not onStreamComplete to avoid duplicate saves
        const result = {
          content: streamState.currentContent,
          sessionId: streamState.currentSessionId,
          messageId: streamState.currentMessageId,
          timestamp: new Date().toISOString(),
        };
        callbacks.onComplete?.(result);
      },
    };
  },

  /**
   * Process individual SSE events
   */
  processSSEEvent: (eventType, data, streamState, callbacks) => {
    // Update session info if present
    if (data.session_id) {
      streamState.currentSessionId = data.session_id;
      streamState.currentMessageId = data.message_id;
    }

    switch (eventType) {
      case 'chunk':
        const chunkContent = data.response || data.delta;
        if (chunkContent) {
          streamState.currentContent += chunkContent;

          // For SDUI contexts, don't show partial JSON
          if (!streamState.isSDUIContext) {
            callbacks.onChunk?.({
              delta: chunkContent,
              content: streamState.currentContent,
              sessionId: streamState.currentSessionId,
              messageId: streamState.currentMessageId,
              isComplete: false,
            });
          }
        }
        break;

      case 'complete':
        if (data.response) {
          streamState.currentContent += data.response;
        }

        // For SDUI responses, parse the JSON and extract the content field
        let displayContent = streamState.currentContent;
        let sduiData = data.sdui;
        let contextData = data.context_data;
        let actionProposal = data.action_proposal;

        // Extract embedded action proposal if not provided by backend
        if (!actionProposal && streamState.currentContent) {
          const extracted = aiService.extractActionProposal(
            streamState.currentContent
          );
          if (extracted.proposal) {
            actionProposal = extracted.proposal;
            displayContent = extracted.cleanContent;
          }
        }

        // If this is an SDUI context or we have SDUI data, try to parse JSON
        if (
          (streamState.isSDUIContext || sduiData || contextData) &&
          displayContent
        ) {
          try {
            // Try to parse the accumulated content as JSON
            const jsonResponse = JSON.parse(displayContent);
            if (jsonResponse.content) {
              displayContent = jsonResponse.content;
              // Also extract SDUI data from the parsed response if not already provided
              if (!sduiData && jsonResponse.data_updates) {
                sduiData = {
                  content: jsonResponse.content,
                  data_updates: jsonResponse.data_updates,
                  ui_components: jsonResponse.ui_components,
                  actions: jsonResponse.actions,
                  next_prompt: jsonResponse.next_prompt,
                  validations: jsonResponse.validations,
                  confidence: jsonResponse.confidence,
                };
              }
              if (!contextData && jsonResponse.data_updates?.contract_data) {
                contextData = jsonResponse.data_updates.contract_data;
              }
            }
          } catch (e) {
            // Not JSON or parsing failed, use raw content
            console.log(
              '[AI Service] Content is not JSON or parsing failed:',
              e
            );
          }
        }

        callbacks.onStreamComplete?.({
          content: displayContent,
          sessionId: streamState.currentSessionId,
          messageId: streamState.currentMessageId,
          model: data.model,
          timestamp: data.timestamp,
          sdui: sduiData,
          context_data: contextData,
          action_proposal: actionProposal,
        });
        break;

      case 'error':
        callbacks.onError?.(new Error(data.error || 'Streaming error'));
        break;

      default:
        // Session updates or other events
        if (data.session_id && !data.response) {
          callbacks.onSessionUpdate?.({
            sessionId: data.session_id,
            messageId: data.message_id,
          });
        }
    }

    // Always call onMessage for debugging/logging
    callbacks.onMessage?.({
      event: eventType,
      data: data,
      content: streamState.currentContent,
      sessionId: streamState.currentSessionId,
      messageId: streamState.currentMessageId,
    });
  },

  /**
   * Get available AI models
   * @returns {Promise} Response with available models
   */
  getAvailableModels: async () => {
    try {
      const response = await api.get('/ai/models');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Failed to get available models:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get available models',
        details: error.response?.data?.details,
      };
    }
  },

  /**
   * Extract action proposal from response content
   * Parses embedded ACTION_PROPOSAL: {...} from AI response text
   * @param {string} content - Response content that may contain embedded proposal
   * @returns {Object} { proposal: Object|null, cleanContent: string }
   */
  extractActionProposal: (content) => {
    if (!content) {
      return { proposal: null, cleanContent: content };
    }

    // Look for ACTION_PROPOSAL: marker
    const actionMarker = 'ACTION_PROPOSAL:';
    const actionIndex = content.indexOf(actionMarker);

    if (actionIndex === -1) {
      return { proposal: null, cleanContent: content };
    }

    // Extract clean content (before ACTION_PROPOSAL)
    const cleanContent = content.substring(0, actionIndex).trim();

    // Extract JSON part (after ACTION_PROPOSAL:)
    let jsonPart = content.substring(actionIndex + actionMarker.length).trim();

    // Remove markdown code blocks if present
    jsonPart = jsonPart.replace(/^```json?\s*/i, '');
    jsonPart = jsonPart.replace(/\s*```$/i, '');
    jsonPart = jsonPart.trim();

    // Handle escaped JSON (when response is double-encoded)
    // Replace \" with " and \n with actual newlines
    if (jsonPart.includes('\\"')) {
      jsonPart = jsonPart.replace(/\\"/g, '"');
      jsonPart = jsonPart.replace(/\\n/g, '\n');
      jsonPart = jsonPart.replace(/\\t/g, '\t');
    }

    // Try to parse JSON
    try {
      const proposal = JSON.parse(jsonPart);

      return { proposal, cleanContent };
    } catch (error) {
      console.error(
        '[AI Service] Failed to parse action proposal JSON:',
        error
      );
      console.error('[AI Service] JSON content:', jsonPart);
      return { proposal: null, cleanContent: content };
    }
  },
};

/**
 * Utility functions for AI service
 */
export const aiUtils = {
  /**
   * Validate input for AI requests
   * @param {string} input - Input to validate
   * @param {string} type - Type of validation ('message', 'context', 'query')
   * @returns {Object} Validation result
   */
  validateInput: (input, type = 'message') => {
    const limits = {
      message: 5000,
      context: 10000,
      query: 1000,
    };

    const errors = [];
    if (!input?.trim()) {
      errors.push(
        `${type.charAt(0).toUpperCase() + type.slice(1)} is required`
      );
    } else if (input.length > (limits[type] || 5000)) {
      errors.push(
        `${
          type.charAt(0).toUpperCase() + type.slice(1)
        } is too long (max ${limits[type].toLocaleString()} characters)`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Format API response for display
   * @param {Object} response - Response from API
   * @param {string} type - Response type ('chat', 'enrichment')
   * @returns {Object} Formatted response
   */
  formatResponse: (response, type = 'chat') => {
    if (!response?.data) {
      return {
        content: `Failed to ${
          type === 'enrichment' ? 'enrich context' : 'get AI response'
        }`,
        model: 'unknown',
        timestamp: new Date().toISOString(),
        error: true,
      };
    }

    const data = response.data;
    return {
      content: data.enriched_response || data.response,
      model: data.model || 'unknown',
      timestamp: data.timestamp || new Date().toISOString(),
      sessionId: data.session_id,
      messageId: data.message_id,
      error: false,
    };
  },

  /**
   * Context-specific chat helpers
   * These are convenience wrappers around the main chat method
   */
  contractBuilderChat: (
    message,
    sessionId,
    contractState,
    stream = false,
    callbacks = {}
  ) =>
    aiService.chat(message, {
      sessionId,
      chatContext: 'contract_builder',
      contextData: contractState,
      stream,
      callbacks,
    }),

  supportChat: (
    message,
    sessionId,
    ticketData = null,
    stream = false,
    callbacks = {}
  ) =>
    aiService.chat(message, {
      sessionId,
      chatContext: 'support',
      contextData: ticketData,
      stream,
      callbacks,
    }),

  projectAssistantChat: (
    message,
    sessionId,
    projectData = null,
    stream = false,
    callbacks = {}
  ) =>
    aiService.chat(message, {
      sessionId,
      chatContext: 'project_assistant',
      contextData: projectData,
      stream,
      callbacks,
    }),

  codeReviewChat: (code, question, sessionId) => {
    const message = question
      ? `${question}\n\nCode:\n${code}`
      : `Please review this code:\n${code}`;

    return aiService.chat(message, {
      sessionId,
      chatContext: 'code_review',
      contextData: { code, question },
    });
  },

  /**
   * Process SDUI response and extract UI updates
   * @param {Object} response - Response from AI with potential SDUI data
   * @returns {Object} Processed UI updates
   */
  processSDUIResponse: (response) => {
    const sdui = response?.sdui;
    const contextData = response?.context_data;

    if (!sdui && !contextData) {
      return {
        content: response?.response || '',
        updates: {},
        actions: [],
      };
    }

    // Process data updates with confidence scores
    const updates = {};

    // Check for data in multiple possible locations
    const dataSource = contextData || sdui?.data_updates?.contract_data || {};

    if (dataSource) {
      Object.entries(dataSource).forEach(([field, data]) => {
        if (typeof data === 'object' && data !== null) {
          // Handle fields with confidence scores
          if ('value' in data && 'confidence' in data) {
            // Use different thresholds based on field importance
            const criticalFields = ['title', 'description', 'paymentType'];
            const threshold = criticalFields.includes(field) ? 0.5 : 0.3;

            // Always update if confidence is above threshold or if value is not null/empty
            if (data.confidence >= threshold) {
              updates[field] = data.value;
            } else if (
              data.value !== null &&
              data.value !== '' &&
              data.value !== undefined
            ) {
              // Include low confidence values but track them
              updates[field] = data.value;
            }
          } else {
            // Handle direct values (arrays, objects without confidence)
            updates[field] = data;
          }
        } else {
          // Handle primitive values
          updates[field] = data;
        }
      });
    }

    // Generate UI components for field updates
    const generatedComponents = [];

    // Create field update components for updated fields
    Object.entries(updates).forEach(([field, value]) => {
      const fieldData = dataSource[field];
      const confidence =
        typeof fieldData === 'object' && fieldData?.confidence
          ? fieldData.confidence
          : 0.8;

      generatedComponents.push({
        type: 'field_update',
        field_id: field,
        properties: {
          message: `Updated to: ${value}`,
          confidence: confidence,
        },
        priority: 1,
        shouldRender: true,
      });
    });

    return {
      content: sdui?.content || response?.response || '',
      updates,
      components: [
        ...generatedComponents,
        ...(sdui?.ui_components || []).map((c) => ({
          ...c,
          shouldRender: c.visibility !== 'hidden',
        })),
      ],
      actions: (sdui?.actions || []).map((a) => ({
        ...a,
        execute: () => aiUtils.executeUIAction(a),
      })),
      nextPrompt: sdui?.next_prompt,
      validations: sdui?.validations || [],
      confidence: sdui?.confidence || 0.5,
    };
  },

  /**
   * Execute UI action from SDUI response
   */
  executeUIAction: (action) => {
    const element = document.getElementById(action.target || action.field_id);

    switch (action.type) {
      case 'highlight':
      case 'field_update':
        if (element) {
          element.classList.add('highlighted');
          const color = action.params?.color || action.properties?.color;
          if (color) element.style.borderColor = color;
          setTimeout(
            () => element.classList.remove('highlighted'),
            action.params?.duration || 3000
          );
        }
        break;

      case 'scroll_to':
        if (element) {
          setTimeout(
            () =>
              element.scrollIntoView({ behavior: 'smooth', block: 'center' }),
            action.delay_ms || 0
          );
        }
        break;

      case 'show_modal':
        break;

      default:
        console.warn('Unknown action type:', action.type);
    }
  },

  /**
   * Apply SDUI field validations
   * @param {Array} validations - Validation rules from SDUI
   * @param {Object} data - Data to validate
   * @returns {Object} Validation errors by field
   */
  applyValidations: (validations, data) => {
    const errors = {};
    const validators = {
      required: (val) => val?.toString().trim().length > 0,
      'min:': (val, param) => parseFloat(val) >= parseFloat(param),
      'max:': (val, param) => parseFloat(val) <= parseFloat(param),
      'minLength:': (val, param) => val?.toString().length >= parseInt(param),
      'maxLength:': (val, param) => val?.toString().length <= parseInt(param),
    };

    (validations || []).forEach(({ field_id, rule, message }) => {
      const value = data[field_id];
      const validator = Object.entries(validators).find(
        ([k]) => rule === k || rule.startsWith(k)
      );

      if (validator) {
        const [, fn] = validator;
        const param = rule.includes(':') ? rule.split(':')[1] : null;
        if (!fn(value, param)) {
          errors[field_id] = message;
        }
      }
    });

    return errors;
  },

  /**
   * Extract confidence scores from AI response
   * Checks multiple possible locations for confidence data
   * @param {Object} response - AI response object
   * @returns {Object} Confidence scores by field
   */
  extractConfidenceScores: (response) => {
    const confidences = {};

    // Define sources to check (in order of priority)
    const sources = [
      response?.context_data,
      response?.sdui?.data_updates?.contract_data,
      response?.contract_update, // Legacy support
    ];

    sources.forEach((source) => {
      if (!source) return;

      Object.entries(source).forEach(([field, data]) => {
        if (data && typeof data === 'object' && data.confidence !== undefined) {
          confidences[field] = data.confidence;
        }
      });
    });

    return confidences;
  },
};

export default aiService;
